import React, { useEffect, useRef } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import * as topojson from 'topojson-client';
import worldAtlas from 'world-atlas/countries-110m.json';

// Unwrap a polygon ring so no consecutive segment spans more than 180° of longitude.
// Coordinates may exceed ±180° — Leaflet's world-copy rendering handles them correctly,
// and this eliminates the horizontal-line artefact for countries crossing the antimeridian.
function unwrapRing(ring) {
  if (ring.length === 0) return ring;
  const out = [[ring[0][0], ring[0][1]]];
  for (let i = 1; i < ring.length; i++) {
    const prev = out[i - 1][0];
    let lng = ring[i][0];
    while (lng - prev > 180) lng -= 360;
    while (lng - prev < -180) lng += 360;
    out.push([lng, ring[i][1]]);
  }
  return out;
}

function unwrapGeometry(geom) {
  if (!geom) return geom;
  if (geom.type === 'Polygon') {
    return { ...geom, coordinates: geom.coordinates.map(unwrapRing) };
  }
  if (geom.type === 'MultiPolygon') {
    return { ...geom, coordinates: geom.coordinates.map(poly => poly.map(unwrapRing)) };
  }
  return geom;
}

// Convert once at module load — synchronous, no fetch, no CDN dependency.
// unwrapGeometry fixes Russia and any other country whose polygon crosses ±180°.
const _raw = topojson.feature(worldAtlas, worldAtlas.objects.countries);
const COUNTRIES_GEOJSON = {
  ..._raw,
  features: _raw.features.map(f => ({ ...f, geometry: unwrapGeometry(f.geometry) })),
};

// ISO 3166-1 numeric → alpha-2
const NUM_TO_ALPHA2 = {
  4:"AF",8:"AL",12:"DZ",20:"AD",24:"AO",28:"AG",31:"AZ",32:"AR",36:"AU",40:"AT",
  44:"BS",48:"BH",50:"BD",51:"AM",52:"BB",56:"BE",64:"BT",68:"BO",70:"BA",72:"BW",
  76:"BR",84:"BZ",90:"SB",96:"BN",100:"BG",104:"MM",108:"BI",112:"BY",116:"KH",
  120:"CM",124:"CA",132:"CV",140:"CF",144:"LK",148:"TD",152:"CL",156:"CN",158:"TW",
  170:"CO",174:"KM",178:"CG",180:"CD",184:"CK",188:"CR",191:"HR",192:"CU",196:"CY",
  203:"CZ",204:"BJ",208:"DK",212:"DM",214:"DO",218:"EC",222:"SV",226:"GQ",231:"ET",
  232:"ER",233:"EE",238:"FK",242:"FJ",246:"FI",250:"FR",262:"DJ",266:"GA",268:"GE",
  270:"GM",276:"DE",288:"GH",296:"KI",300:"GR",308:"GD",320:"GT",324:"GN",328:"GY",
  332:"HT",340:"HN",344:"HK",348:"HU",352:"IS",356:"IN",360:"ID",364:"IR",368:"IQ",
  372:"IE",376:"IL",380:"IT",384:"CI",388:"JM",392:"JP",398:"KZ",400:"JO",404:"KE",
  408:"KP",410:"KR",414:"KW",417:"KG",418:"LA",422:"LB",426:"LS",428:"LV",430:"LR",
  434:"LY",438:"LI",440:"LT",442:"LU",450:"MG",454:"MW",458:"MY",462:"MV",466:"ML",
  470:"MT",478:"MR",480:"MU",484:"MX",492:"MC",496:"MN",498:"MD",499:"ME",504:"MA",
  508:"MZ",512:"OM",516:"NA",520:"NR",524:"NP",528:"NL",540:"NC",548:"VU",554:"NZ",
  558:"NI",562:"NE",566:"NG",578:"NO",583:"FM",585:"PW",586:"PK",591:"PA",598:"PG",
  600:"PY",604:"PE",608:"PH",616:"PL",620:"PT",624:"GW",626:"TL",630:"PR",634:"QA",
  642:"RO",643:"RU",646:"RW",674:"SM",678:"ST",682:"SA",686:"SN",688:"RS",690:"SC",
  694:"SL",702:"SG",703:"SK",704:"VN",705:"SI",706:"SO",710:"ZA",716:"ZW",724:"ES",
  728:"SS",729:"SD",740:"SR",748:"SZ",752:"SE",756:"CH",760:"SY",762:"TJ",764:"TH",
  768:"TG",776:"TO",780:"TT",784:"AE",788:"TN",792:"TR",795:"TM",798:"TV",800:"UG",
  804:"UA",807:"MK",818:"EG",826:"GB",834:"TZ",840:"US",854:"BF",858:"UY",860:"UZ",
  862:"VE",882:"WS",887:"YE",894:"ZM",
};

const COUNTRY_NAMES = {
  AF:'Afghanistan',AL:'Albania',DZ:'Algeria',AO:'Angola',AR:'Argentina',AM:'Armenia',
  AU:'Australia',AT:'Austria',AZ:'Azerbaijan',BD:'Bangladesh',BY:'Belarus',BE:'Belgium',
  BJ:'Benin',BO:'Bolivia',BA:'Bosnia',BW:'Botswana',BR:'Brazil',BG:'Bulgaria',
  BF:'Burkina Faso',BI:'Burundi',KH:'Cambodia',CM:'Cameroon',CA:'Canada',CF:'Central African Rep.',
  TD:'Chad',CL:'Chile',CN:'China',CO:'Colombia',CG:'Congo',CD:'DR Congo',CR:'Costa Rica',
  CI:"Côte d'Ivoire",HR:'Croatia',CU:'Cuba',CY:'Cyprus',CZ:'Czechia',DK:'Denmark',
  DJ:'Djibouti',DO:'Dominican Rep.',EC:'Ecuador',EG:'Egypt',SV:'El Salvador',GQ:'Equatorial Guinea',
  ER:'Eritrea',EE:'Estonia',ET:'Ethiopia',FI:'Finland',FR:'France',GA:'Gabon',GM:'Gambia',
  GE:'Georgia',DE:'Germany',GH:'Ghana',GR:'Greece',GT:'Guatemala',GN:'Guinea',GW:'Guinea-Bissau',
  HT:'Haiti',HN:'Honduras',HU:'Hungary',IN:'India',ID:'Indonesia',IR:'Iran',IQ:'Iraq',
  IE:'Ireland',IL:'Israel',IT:'Italy',JM:'Jamaica',JP:'Japan',JO:'Jordan',KZ:'Kazakhstan',
  KE:'Kenya',KP:'North Korea',KR:'South Korea',KW:'Kuwait',KG:'Kyrgyzstan',LA:'Laos',
  LV:'Latvia',LB:'Lebanon',LS:'Lesotho',LR:'Liberia',LY:'Libya',LT:'Lithuania',
  MG:'Madagascar',MW:'Malawi',MY:'Malaysia',ML:'Mali',MR:'Mauritania',MX:'Mexico',
  MD:'Moldova',MN:'Mongolia',MA:'Morocco',MZ:'Mozambique',MM:'Myanmar',NA:'Namibia',
  NP:'Nepal',NL:'Netherlands',NZ:'New Zealand',NI:'Nicaragua',NE:'Niger',NG:'Nigeria',
  MK:'North Macedonia',NO:'Norway',OM:'Oman',PK:'Pakistan',PA:'Panama',PG:'Papua New Guinea',
  PY:'Paraguay',PE:'Peru',PH:'Philippines',PL:'Poland',PT:'Portugal',QA:'Qatar',
  RO:'Romania',RU:'Russia',RW:'Rwanda',SA:'Saudi Arabia',SN:'Senegal',RS:'Serbia',
  SL:'Sierra Leone',SO:'Somalia',ZA:'South Africa',SS:'South Sudan',ES:'Spain',LK:'Sri Lanka',
  SD:'Sudan',SR:'Suriname',SZ:'Eswatini',SE:'Sweden',CH:'Switzerland',SY:'Syria',
  TW:'Taiwan',TJ:'Tajikistan',TZ:'Tanzania',TH:'Thailand',TL:'Timor-Leste',TG:'Togo',
  TN:'Tunisia',TR:'Turkey',TM:'Turkmenistan',UG:'Uganda',UA:'Ukraine',AE:'UAE',
  GB:'United Kingdom',US:'United States',UY:'Uruguay',UZ:'Uzbekistan',VE:'Venezuela',
  VN:'Vietnam',YE:'Yemen',ZM:'Zambia',ZW:'Zimbabwe',
};

const COUNTRY_COORDS = {
  AF:[33.93,67.71],AL:[41.15,20.17],DZ:[28.03,1.66],AO:[-11.2,17.87],AR:[-38.42,-63.62],
  AM:[40.07,45.04],AU:[-25.27,133.78],AT:[47.52,14.55],AZ:[40.14,47.58],BD:[23.68,90.36],
  BY:[53.71,27.95],BE:[50.50,4.47],BJ:[9.31,2.32],BO:[-16.29,-63.59],BA:[43.92,17.68],
  BW:[-22.33,24.68],BR:[-14.24,-51.93],BG:[42.73,25.49],BF:[12.36,-1.56],BI:[-3.37,29.92],
  KH:[12.57,104.99],CM:[3.85,11.50],CA:[56.13,-106.35],CF:[6.61,20.94],TD:[15.45,18.73],
  CL:[-35.68,-71.54],CN:[35.86,104.20],CO:[4.57,-74.30],CG:[-0.23,15.83],CD:[-4.04,21.76],
  CR:[9.75,-83.75],CI:[7.54,-5.55],HR:[45.10,15.20],CU:[21.52,-77.78],CY:[35.13,33.43],
  CZ:[49.82,15.47],DK:[56.26,9.50],DJ:[11.83,42.59],DO:[18.74,-70.16],EC:[-1.83,-78.18],
  EG:[26.82,30.80],SV:[13.79,-88.90],GQ:[1.65,10.27],ER:[15.18,39.78],EE:[58.60,25.01],
  ET:[9.15,40.49],FJ:[-16.58,179.41],FI:[61.92,25.75],FR:[46.23,2.21],GA:[-0.80,11.61],
  GM:[13.44,-15.31],GE:[42.32,43.36],DE:[51.17,10.45],GH:[7.95,-1.02],GR:[39.07,21.82],
  GT:[15.78,-90.23],GN:[9.95,-11.61],GW:[11.80,-15.18],HT:[18.97,-72.29],HN:[15.20,-86.24],
  HU:[47.16,19.50],IN:[20.59,78.96],ID:[-0.79,113.92],IR:[32.43,53.69],IQ:[33.22,43.68],
  IE:[53.41,-8.24],IL:[31.05,34.85],IT:[41.87,12.57],JM:[18.11,-77.30],JP:[36.20,138.25],
  JO:[30.59,36.24],KZ:[48.02,66.92],KE:[-0.02,37.91],KP:[40.34,127.51],KR:[35.91,127.77],
  KW:[29.31,47.48],KG:[41.20,74.77],LA:[19.86,102.50],LV:[56.88,24.60],LB:[33.85,35.86],
  LS:[-29.61,28.23],LR:[6.43,-9.43],LY:[26.34,17.23],LT:[55.17,23.88],MG:[-18.77,46.87],
  MW:[-13.25,34.30],MY:[4.21,108.96],ML:[17.57,-3.99],MR:[21.01,-10.94],MX:[23.63,-102.55],
  MD:[47.41,28.37],MN:[46.86,103.85],MA:[31.79,-7.09],MZ:[-18.67,35.53],MM:[21.91,95.96],
  NA:[-22.96,18.49],NP:[28.39,84.12],NL:[52.13,5.29],NZ:[-40.90,174.89],NI:[12.87,-85.21],
  NE:[17.61,8.08],NG:[9.08,8.68],MK:[41.61,21.75],NO:[60.47,8.47],OM:[21.51,55.92],
  PK:[30.38,69.35],PA:[8.54,-80.78],PG:[-6.31,143.96],PY:[-23.44,-58.44],PE:[-9.19,-75.02],
  PH:[12.88,121.77],PL:[51.92,19.15],PT:[39.40,-8.22],QA:[25.35,51.18],RO:[45.94,24.97],
  RU:[61.52,105.32],RW:[-1.94,29.87],SA:[23.89,45.08],SN:[14.50,-14.45],RS:[44.02,21.01],
  SL:[8.46,-11.78],SO:[5.15,46.20],ZA:[-30.56,22.94],SS:[6.88,31.31],ES:[40.46,-3.75],
  LK:[7.87,80.77],SD:[12.86,30.22],SR:[3.92,-56.03],SZ:[-26.52,31.47],SE:[60.13,18.64],
  CH:[46.82,8.23],SY:[34.80,38.99],TW:[23.70,120.96],TJ:[38.86,71.28],TZ:[-6.37,34.89],
  TH:[15.87,100.99],TL:[-8.87,125.73],TG:[8.62,0.82],TN:[33.89,9.54],TR:[38.96,35.24],
  TM:[38.97,59.56],UG:[1.37,32.29],UA:[48.38,31.17],AE:[23.42,53.85],GB:[55.38,-3.44],
  US:[37.09,-95.71],UY:[-32.52,-55.77],UZ:[41.38,64.59],VE:[6.42,-66.59],VN:[14.06,108.28],
  YE:[15.55,48.52],ZM:[-13.13,27.85],ZW:[-19.02,29.15],
};

function countryFlag(code) {
  if (!code || code.length !== 2) return '';
  return String.fromCodePoint(...code.toUpperCase().split('').map(c => 127397 + c.charCodeAt(0)));
}

function featureStyle(code, focusCountry, affectedCountry, activeCountries, allEventCountries) {
  if (code === focusCountry) {
    return { fillColor: '#1d9bf0', weight: 1.5, color: '#1d9bf0', fillOpacity: 0.35 };
  }
  if (affectedCountry && code === affectedCountry) {
    return { fillColor: '#ff7a00', weight: 1.5, color: '#ff7a00', fillOpacity: 0.35 };
  }
  if (activeCountries.includes(code)) {
    return { fillColor: '#00ba7c', weight: 1, color: '#00ba7c', fillOpacity: 0.25 };
  }
  if (allEventCountries && allEventCountries.includes(code)) {
    return { fillColor: '#a78bfa', weight: 0.8, color: '#a78bfa', fillOpacity: 0.15 };
  }
  return { weight: 0.4, color: 'rgba(120,120,120,0.3)', fillOpacity: 0 };
}

function tooltipContent(code, focusCountry, affectedCountry, activeCountries) {
  const name = COUNTRY_NAMES[code] || code;
  const flagHtml = `<img src="https://flagcdn.com/w20/${code.toLowerCase()}.png" height="13" style="vertical-align:middle;border-radius:2px;margin-right:4px;">`;
  let role = '';
  if (code === focusCountry) role = ' · <strong>Incident</strong>';
  else if (affectedCountry && code === affectedCountry) role = ' · <strong>Affected</strong>';
  else if (activeCountries.includes(code)) role = ' · <em>Responding</em>';
  return `${flagHtml}${name}${role}`;
}

function MapView({ focusCountry, affectedCountry, activeCountries = [], allEventCountries = [], onCountryClick }) {
  const mapRef = useRef(null);
  const leafletRef = useRef(null);
  const geoLayerRef = useRef(null);

  // Keep latest props in refs so the style-update effect always reads current values
  const propsRef = useRef({ focusCountry, affectedCountry, activeCountries, allEventCountries, onCountryClick });
  propsRef.current = { focusCountry, affectedCountry, activeCountries, allEventCountries, onCountryClick };

  // ── Initialise map + GeoJSON layer (once, deferred until container has size) ──
  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;

    const handleResize = () => {
      // Bail if container still has no rendered area (e.g. display:none parent)
      if (!el.offsetWidth || !el.offsetHeight) return;

      if (leafletRef.current) {
        // Map already exists — fix tile rendering after the container was
        // hidden (mobile tab-switch) and is now visible again.
        leafletRef.current.invalidateSize(false);
        return;
      }

      // ── First-time init ──────────────────────────────────────────────────
      try {
        const map = L.map(el, {
          center: [20, 10],
          zoom: 2,
          minZoom: 1,
          maxZoom: 8,
          scrollWheelZoom: false,
          zoomControl: true,
          attributionControl: true,
        });

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          maxZoom: 19,
        }).addTo(map);

        const { focusCountry: fc, affectedCountry: ac, activeCountries: act, allEventCountries: aec } = propsRef.current;

        const layer = L.geoJSON(COUNTRIES_GEOJSON, {
          style: feature => {
            const code = NUM_TO_ALPHA2[feature.id];
            if (!code) return { weight: 0.3, color: 'rgba(120,120,120,0.2)', fillOpacity: 0 };
            return featureStyle(code, fc, ac, act, aec);
          },
          onEachFeature: (feature, lyr) => {
            const code = NUM_TO_ALPHA2[feature.id];
            if (!code) return;
            lyr.bindTooltip(tooltipContent(code, fc, ac, act), {
              sticky: true,
              className: 'map-country-tooltip',
              direction: 'top',
            });
            lyr.on('click', () => {
              const cb = propsRef.current.onCountryClick;
              if (cb) cb(code);
            });
            lyr.on('mouseover', function() {
              const cb = propsRef.current.onCountryClick;
              if (cb) this.getElement()?.style && (this.getElement().style.cursor = 'pointer');
            });
          },
        }).addTo(map);

        leafletRef.current = map;
        geoLayerRef.current = layer;

        if (fc && COUNTRY_COORDS[fc]) {
          map.setView(COUNTRY_COORDS[fc], 3);
        }
      } catch (err) {
        console.warn('[MapView] Leaflet init error:', err);
      }
    };

    // Keep observer alive for the full component lifetime so it can call
    // invalidateSize whenever the panel is hidden then re-shown on mobile.
    const observer = new ResizeObserver(handleResize);
    observer.observe(el);
    handleResize(); // try immediately — works on desktop where container is already visible

    return () => {
      observer.disconnect();
      if (leafletRef.current) {
        leafletRef.current.remove();
        leafletRef.current = null;
        geoLayerRef.current = null;
      }
    };
  }, []); // eslint-disable-line

  // ── Re-style whenever selected event changes ────────────────────────────
  useEffect(() => {
    const map = leafletRef.current;
    const layer = geoLayerRef.current;
    if (!map || !layer) return;

    layer.setStyle(feature => {
      const code = NUM_TO_ALPHA2[feature.id];
      if (!code) return { weight: 0.3, color: 'rgba(120,120,120,0.2)', fillOpacity: 0 };
      return featureStyle(code, focusCountry, affectedCountry, activeCountries, allEventCountries);
    });

    layer.eachLayer(lyr => {
      const code = NUM_TO_ALPHA2[lyr.feature?.id];
      if (!code || !lyr.getTooltip()) return;
      lyr.setTooltipContent(tooltipContent(code, focusCountry, affectedCountry, activeCountries));
    });

    try {
      if (focusCountry && COUNTRY_COORDS[focusCountry]) {
        map.flyTo(COUNTRY_COORDS[focusCountry], 4, { animate: true, duration: 1.0 });
      } else {
        map.setView([20, 10], 2);
      }
    } catch (err) {
      console.warn('[MapView] flyTo error:', err);
    }
  }, [focusCountry, affectedCountry, activeCountries]); // eslint-disable-line

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <style>{`
        .map-country-tooltip {
          background: var(--bg-card, #fff);
          border: 1px solid var(--border, #e2e8f0);
          border-radius: 6px;
          padding: 4px 8px;
          font-size: 0.8125rem;
          color: var(--text-primary, #0f1419);
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          white-space: nowrap;
        }
        .map-country-tooltip::before { display: none; }
      `}</style>
      <div ref={mapRef} style={{ width: '100%', height: '100%', borderRadius: 'inherit' }} />
    </div>
  );
}

export default MapView;
